import { useEffect, useState } from 'react';
import { globalEmitter, emitterProps } from '../helpers/emitter';
import { wishlistProviderService } from '../helpers/provider.service';
import { OmneoList, omneoListEmpty } from '../types';
import { DashboardError } from './DashboardError';
import { WishlistDetailContent, WishlistDetailHeader } from './WishlistDetail';
import { WishlistListContent, WishlistListHeader } from './WishlistList';
import { LoaderSpin } from './LoaderSpin';

export const WISHLIST_PAGE = {
  allItems: 'all-items',
  lists: 'list',
  detail: 'detail'
} as const

export type WishlistPage = typeof WISHLIST_PAGE[keyof typeof WISHLIST_PAGE]
export type WishlistPageIds = typeof WISHLIST_PAGE

type SetDashboardPage = (nextPage: WishlistPage) => void

export function WishlistDashboard ({showListPageTitle = true}: {showListPageTitle?: boolean}) {
  const [isLoading, setIsLoading] = useState(true)
  const [isAccount, setIsAccount] = useState(true)
  const [isProviderReady, setIsProviderReady] = useState(true)
  const [page, setPage] = useState<WishlistPage>(WISHLIST_PAGE.lists)
  const [lists, setLists] = useState<OmneoList[]>([])
  const [currentList, setCurrentList] = useState<OmneoList>(omneoListEmpty)
  const [changeList, setChangeList] = useState(true)

  const urlParams = new URL(window.location.href)

  const fetchLists = async () => {
    const lists = await wishlistProviderService.getLists()
    setLists(lists)
  }

  const updateLists = (list: OmneoList) => {
    const _lists = lists.map((item: OmneoList) => {
      if (item.id === list.id) {
        item = list
      }
      return item
    })
    setLists(_lists)
  }

  const deleteList = (list: OmneoList) => {
    const _lists = lists.filter((item: OmneoList) => item.id !== list.id)
    setLists(_lists)
    setPage(WISHLIST_PAGE.lists)
  }

  const setDashboardPage: SetDashboardPage = (nextPage) => {
    const url = new URL(window.location.href)

    if (nextPage === WISHLIST_PAGE.allItems) {
      url.hash = `#view=${WISHLIST_PAGE.allItems}`
      window.history.pushState({}, '', url)
    } else if (nextPage === WISHLIST_PAGE.lists) {
      url.hash = '#view=lists'
      window.history.pushState({}, '', url)
    }

    // For detail navigation, callers are responsible for setting the hash with the list id.
    setPage(nextPage)
  }

  const allItemsList = {
    ...omneoListEmpty,
    id: WISHLIST_PAGE.allItems,
    name: 'All Items',
    items: lists.flatMap((list) => list.items),
    definition: lists[0]?.definition || omneoListEmpty.definition
  }

  useEffect(() => {
    const handleCreateNewList = () => {
      setChangeList((currentValue) => !currentValue)
    }
    const handleWishlistRemove = () => {
      setChangeList((currentValue) => !currentValue)
    }

    globalEmitter.on('createNewList', handleCreateNewList)
    globalEmitter.on(emitterProps.wishlist.remove, handleWishlistRemove)

    return () => {
      globalEmitter.off('createNewList', handleCreateNewList)
      globalEmitter.off(emitterProps.wishlist.remove, handleWishlistRemove)
    }
  }, [])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      ;(async () => {
        const isLoggedIn = await wishlistProviderService.isLoggedIn()
        const providerReady = wishlistProviderService.isReady()

        setIsAccount(isLoggedIn)
        setIsProviderReady(providerReady)

        if (isLoggedIn && providerReady) {
          await fetchLists()
        }

        setIsLoading(false)
      })()
    }, 500)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, []);

  useEffect(() => {
    const refreshLists = async () => {
      const loggedIn = await wishlistProviderService.isLoggedIn()
      const providerReady = wishlistProviderService.isReady()

      setIsAccount(loggedIn)
      setIsProviderReady(providerReady)

      if (!providerReady || !loggedIn) {
        return
      }

      await fetchLists()
    }

    const detachReady = wishlistProviderService.onReady(() => {
      refreshLists().then()
    })
    const detachProviderEvents = wishlistProviderService.subscribeToListChanges(() => {
      refreshLists().then()
    })

    return () => {
      detachReady()
      detachProviderEvents()
    }
  }, [])

  useEffect(() => {
    if (!lists.length) return
    const idListItemByHashUrl = urlParams.hash.replace('#view=detail&id=', '')
    if (idListItemByHashUrl) {
      const listWithHandle = lists.find(list => list.id.toString() === idListItemByHashUrl)
      if (listWithHandle) {
        setCurrentList(listWithHandle)
        setPage(WISHLIST_PAGE.detail)
      }
    }

    if (window.location.hash === '') {
      window.location.hash = '#view=lists'
    }

    const handleHashChange = () => {
      const hashUrl = window.location.hash

      if (hashUrl) {
        if (hashUrl.includes('#view=lists')) {
          setPage(WISHLIST_PAGE.lists)
        } else if (hashUrl.includes(`#view=${WISHLIST_PAGE.allItems}`)) {
          setPage(WISHLIST_PAGE.allItems)
        } else if (hashUrl.includes('#view=detail&id=')) {
          const idList = hashUrl.replace('#view=detail&id=', '')
          const listWithHandle = idList && lists.find(list => list.id.toString() === idList)
          if (listWithHandle) {
            setCurrentList(listWithHandle)
            setPage(WISHLIST_PAGE.detail)
          }
        }
      }
    }

    window.addEventListener('hashchange', handleHashChange, false)

    return () => {
      window.removeEventListener('hashchange', handleHashChange, false)
    }
  }, [lists]);

  useEffect(() => {
    const handleUpdateList = (payload: { list: OmneoList }) => {
      setCurrentList(payload.list)
      updateLists(payload.list)
    }

    const handleDeleteList = (payload: { list: OmneoList }) => {
      deleteList(payload.list)
    }

    globalEmitter.on('updateList', handleUpdateList)
    globalEmitter.on('deleteList', handleDeleteList)

    return () => {
      globalEmitter.off('updateList', handleUpdateList)
      globalEmitter.off('deleteList', handleDeleteList)
    }
  }, [lists])

  useEffect(() => {
    if (!isAccount || !isProviderReady) {
      return
    }

    (async () => {
      await fetchLists()
    })()
  }, [changeList, isAccount, isProviderReady]);

  return (
    <div>
      {isLoading ? <div className="relative bg-background-light py-[100px]"><LoaderSpin classSize="w-10 h-10" /></div> :
        <>
          <RenderWishlistHeader lists={lists} page={page} currentList={currentList} pageIds={WISHLIST_PAGE} setPage={setDashboardPage} showListPageTitle={showListPageTitle} />
          <div className="relative pb-0 overflow-hidden bg-background-light lg:pb-20">
            {!isLoading && !isAccount && <div>Account not validate</div>}
            {!isLoading && isAccount && !isProviderReady && (
              <DashboardError errorMessage="Wishlist is temporarily unavailable." />
            )}
            {!isLoading && isAccount && isProviderReady && <RenderWishlistContent page={page} lists={lists} currentList={currentList} allItemsList={allItemsList} pageIds={WISHLIST_PAGE} setPage={setDashboardPage} setCurrentList={setCurrentList} />}
          </div>
        </>
      }
    </div>
  )
}

type headerProps = {
  lists: OmneoList[]
  page: WishlistPage
  pageIds: WishlistPageIds
  setPage: SetDashboardPage
  currentList: OmneoList
}

function RenderWishlistHeader ({ lists, page, currentList, pageIds, setPage, showListPageTitle }: headerProps & {showListPageTitle: boolean}) {
  switch (page) {
    case WISHLIST_PAGE.detail:
      return <WishlistDetailHeader lists={lists} currentList={currentList} pageIds={pageIds} setPage={setPage} />
    default:
      return <WishlistListHeader lists={lists} page={page} pageIds={pageIds} setPage={setPage} showPageTitle={showListPageTitle} />
  }
}

type contentProps = {
  page: WishlistPage
  lists: OmneoList[]
  currentList: OmneoList
  allItemsList: OmneoList
  pageIds: WishlistPageIds
  setPage: SetDashboardPage
  setCurrentList: React.Dispatch<React.SetStateAction<OmneoList>>
}

function RenderWishlistContent ({ page, lists, currentList, allItemsList, pageIds, setPage, setCurrentList }: contentProps) {
  switch (page) {
    case WISHLIST_PAGE.detail:
      return <WishlistDetailContent key={currentList.id} currentList={currentList} lists={lists} pageIds={pageIds} />
    case WISHLIST_PAGE.allItems:
      return <WishlistDetailContent key={allItemsList.id} currentList={allItemsList} lists={lists} pageIds={pageIds} />
    default:
      return <WishlistListContent lists={lists} pageIds={pageIds} setPage={setPage} setCurrentList={setCurrentList} />
  }
}
